import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  HeadingText,
  Modal,
  ParagraphText,
  StackLayout,
  UtilityText,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';

const MODAL_CONTENT_WIDTH = 668;

interface CopyAllConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (includeDrafts: boolean) => void;
  entityName: string;
  environmentName: string;
}

export const CopyAllConfirmationModal: React.FC<
  CopyAllConfirmationModalProps
> = ({ open, onClose, onConfirm, entityName, environmentName }) => {
  const { formatMessage } = useIntl();
  const [includeDrafts, setIncludeDrafts] = React.useState(false);

  const handleOpenChange = React.useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        onClose();
        setIncludeDrafts(false);
      }
    },
    [onClose],
  );

  const handleConfirm = React.useCallback(() => {
    onConfirm(includeDrafts);
  }, [onConfirm, includeDrafts]);

  return (
    <Modal.Root open={open} onOpenChange={handleOpenChange}>
      <Modal.Content width={MODAL_CONTENT_WIDTH} padding="$two">
        <StackLayout spacing="$one" horizontalAlignment="stretch">
          <Modal.Title>
            <HeadingText level="2" size="xl">
              {formatMessage(
                { id: 'COPY_ALL_CONFIRMATION_TITLE' },
                { entityName, environmentName },
              )}
            </HeadingText>
          </Modal.Title>
          <Modal.Description>
            <Box>
              <ParagraphText>
                {formatMessage(
                  { id: 'COPY_ALL_CONFIRMATION_DESCRIPTION' },
                  { entityName, environmentName },
                )}
              </ParagraphText>
            </Box>
          </Modal.Description>
          <Checkbox
            id="include-drafts"
            name="include-drafts"
            label={
              <UtilityText>
                {formatMessage(
                  { id: 'COPY_ALL_CONFIRMATION_INCLUDE_DRAFTS' },
                  { entityName },
                )}
              </UtilityText>
            }
            checked={includeDrafts}
            onChange={() => setIncludeDrafts(!includeDrafts)}
          />
          <StackLayout
            orientation="horizontal"
            horizontalAlignment="end"
            spacing="$one"
          >
            <Modal.Close>
              <Button priority="tertiary" quiet>
                {formatMessage({ id: 'CANCEL' })}
              </Button>
            </Modal.Close>
            <Button onClick={handleConfirm}>
              {formatMessage({ id: 'COPY_ALL_CONFIRMATION_CONFIRM' })}
            </Button>
          </StackLayout>
        </StackLayout>
      </Modal.Content>
    </Modal.Root>
  );
};
